/**
 * CategoriesContent - Gestão de categorias do cardápio
 * Extraído VERBATIM do App.js (Fase 2 da refatoração — zero mudança de comportamento).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusCircle, Edit, Trash2, Tag, Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { getBackendUrl } from '../../utils/api';

const API = `${getBackendUrl()}/api`;

const COLOR_OPTIONS = [
  { value: '#ef4444', label: 'Vermelho', color: '#ef4444' },
  { value: '#f59e0b', label: 'Laranja', color: '#f59e0b' },
  { value: '#eab308', label: 'Amarelo', color: '#eab308' },
  { value: '#22c55e', label: 'Verde', color: '#22c55e' },
  { value: '#06b6d4', label: 'Ciano', color: '#06b6d4' },
  { value: '#3b82f6', label: 'Azul', color: '#3b82f6' },
  { value: '#8b5cf6', label: 'Roxo', color: '#8b5cf6' },
  { value: '#ec4899', label: 'Rosa', color: '#ec4899' },
  { value: '#6b7280', label: 'Cinza', color: '#6b7280' }
];


const CategoriesContent = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Dias da semana em português
  const WEEKDAYS = [
    { id: 'segunda', label: 'Segunda' },
    { id: 'terça', label: 'Terça' },
    { id: 'quarta', label: 'Quarta' },
    { id: 'quinta', label: 'Quinta' },
    { id: 'sexta', label: 'Sexta' },
    { id: 'sábado', label: 'Sábado' },
    { id: 'domingo', label: 'Domingo' }
  ];
  
  const ALL_DAYS = WEEKDAYS.map(d => d.id);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6',
    position: '',
    available_days: ALL_DAYS,
    available_from: '',
    available_until: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.available_days || formData.available_days.length === 0) {
      alert('Selecione ao menos um dia da semana');
      return;
    }
    
    const hasFrom = !!(formData.available_from || '').trim();
    const hasUntil = !!(formData.available_until || '').trim();
    if (hasFrom !== hasUntil) {
      alert('Preencha tanto o horário inicial quanto o final, ou deixe ambos vazios para disponível o dia todo');
      return;
    }
    
    try {
      const submitData = {
        ...formData,
        position: formData.position && formData.position.trim() !== '' ? parseInt(formData.position) : null,
        available_from: hasFrom ? formData.available_from : null,
        available_until: hasUntil ? formData.available_until : null
      };
      
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, submitData);
      } else {
        await axios.post(`${API}/categories`, submitData);
      }
      setIsModalOpen(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', color: '#3b82f6', position: '', available_days: ALL_DAYS, available_from: '', available_until: '' });
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      alert(error.response?.data?.detail || 'Erro ao salvar categoria');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      description: category.description || '',
      color: category.color || '#FF6B35',
      position: category.position != null ? category.position.toString() : '',
      available_days: category.available_days || ALL_DAYS,
      available_from: category.available_from || '',
      available_until: category.available_until || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('Tem certeza que deseja excluir esta categoria?')) {
      try {
        await axios.delete(`${API}/categories/${categoryId}`);
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Erro ao excluir categoria. Verifique se não há produtos associados.');
      }
    }
  };

  const openModal = () => {
    setEditingCategory(null);
    setFormData({ name: '', description: '', color: '#3b82f6', position: '', available_days: ALL_DAYS, available_from: '', available_until: '' });
    setIsModalOpen(true);
  };
  
  const toggleDay = (dayId) => {
    const currentDays = formData.available_days || [];
    if (currentDays.includes(dayId)) {
      // Não permitir remover se for o último dia
      if (currentDays.length === 1) {
        alert('A categoria deve estar disponível em pelo menos um dia');
        return;
      }
      setFormData({
        ...formData,
        available_days: currentDays.filter(d => d !== dayId)
      });
    } else {
      setFormData({
        ...formData,
        available_days: [...currentDays, dayId]
      });
    }
  };
  
  const selectAllDays = () => {
    setFormData({ ...formData, available_days: ALL_DAYS });
  };
  
  const selectWeekdays = () => {
    setFormData({ ...formData, available_days: ['segunda', 'terça', 'quarta', 'quinta', 'sexta'] });
  };
  
  const selectWeekend = () => {
    setFormData({ ...formData, available_days: ['sábado', 'domingo'] });
  };
  
  // Função para formatar os dias disponíveis para exibição
  const formatAvailableDays = (days) => {
    if (!days || days.length === 0) return 'Nenhum dia';
    if (days.length === 7) return 'Todos os dias';
    
    const weekdaysOnly = ['segunda', 'terça', 'quarta', 'quinta', 'sexta'];
    const weekendOnly = ['sábado', 'domingo'];
    
    if (days.length === 5 && weekdaysOnly.every(d => days.includes(d))) {
      return 'Dias úteis';
    }
    if (days.length === 2 && weekendOnly.every(d => days.includes(d))) {
      return 'Final de semana';
    }
    
    // Abreviar os dias
    const abbrev = {
      'segunda': 'Seg',
      'terça': 'Ter',
      'quarta': 'Qua',
      'quinta': 'Qui',
      'sexta': 'Sex',
      'sábado': 'Sáb',
      'domingo': 'Dom'
    };
    
    return days.map(d => abbrev[d] || d).join(', ');
  };

  if (loading) {
    return (
      <div className="glass-card">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">Categorias</h1>
        <button onClick={openModal} className="glass-button-primary">
          <PlusCircle size={18} className="mr-2" />
          Nova Categoria
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <div key={category.id} className="glass-card category-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: category.color }}
                >
                  <Tag size={24} className="text-white" />
                </div>
                {category.position && (
                  <div className="flex items-center justify-center w-8 h-8 bg-gray-700 rounded-full">
                    <span className="text-xs font-bold text-white">{category.position}</span>
                  </div>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(category)}
                  className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => handleDelete(category.id)}
                  className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">{category.name}</h3>
            <p className="text-gray-400 text-sm mb-2">{category.description}</p>
            
            {/* Exibir dias disponíveis */}
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={14} className="text-blue-400" />
              <span className="text-xs text-blue-300">
                {formatAvailableDays(category.available_days)}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {category.products_count || 0} produtos
              </span>
              <span 
                className="px-2 py-1 rounded-full text-xs text-white"
                style={{ backgroundColor: category.color }}
              >
                Ativa
              </span>
            </div>
          </div>
        ))}
      </div>

      {categories.length === 0 && (
        <div className="glass-card text-center py-12">
          <Tag size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Nenhuma categoria encontrada</h3>
          <p className="text-gray-400 mb-6">Crie sua primeira categoria para organizar seus produtos</p>
          <button onClick={openModal} className="glass-button-primary">
            Criar Primeira Categoria
          </button>
        </div>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="glass-modal max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white">
              {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-4 pr-2 -mr-2 pb-4" style={{ maxHeight: 'calc(90vh - 140px)', WebkitOverflowScrolling: 'touch' }}>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nome</label>
              <input
                type="text"
                required
                className="glass-input w-full"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Nome da categoria"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
              <textarea
                className="glass-input w-full resize-none"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descrição da categoria"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Posição <span className="text-xs text-gray-500">(opcional)</span>
              </label>
              <input
                type="number"
                className="glass-input w-full"
                value={formData.position}
                onChange={(e) => setFormData({...formData, position: e.target.value})}
                placeholder="Deixe vazio para adicionar ao final"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Cor</label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 ${
                      formData.color === color.value 
                        ? 'border-white scale-110' 
                        : 'border-gray-600 hover:border-gray-400'
                    } transition-all`}
                    style={{ backgroundColor: color.color }}
                    onClick={() => setFormData({...formData, color: color.value})}
                  />
                ))}
              </div>
            </div>
            
            {/* Dias da Semana Disponíveis */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Calendar size={16} className="inline mr-2" />
                Dias Disponíveis
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Em quais dias esta categoria estará visível
              </p>
              
              {/* Botões de atalho */}
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  onClick={selectAllDays}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                    formData.available_days?.length === 7
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Todos
                </button>
                <button
                  type="button"
                  onClick={selectWeekdays}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                    formData.available_days?.length === 5 && 
                    ['segunda', 'terça', 'quarta', 'quinta', 'sexta'].every(d => formData.available_days?.includes(d))
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Seg-Sex
                </button>
                <button
                  type="button"
                  onClick={selectWeekend}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                    formData.available_days?.length === 2 && 
                    ['sábado', 'domingo'].every(d => formData.available_days?.includes(d))
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  Sáb-Dom
                </button>
              </div>
              
              {/* Checkboxes dos dias - Grid otimizado para mobile */}
              <div className="grid grid-cols-7 gap-1">
                {WEEKDAYS.map((day) => (
                  <button
                    key={day.id}
                    type="button"
                    onClick={() => toggleDay(day.id)}
                    className={`p-2 rounded-lg text-center transition-all ${
                      formData.available_days?.includes(day.id)
                        ? 'bg-green-600 text-white border-2 border-green-400'
                        : 'bg-gray-700 text-gray-400 border-2 border-gray-600'
                    }`}
                  >
                    <span className="text-[10px] sm:text-xs font-medium block">{day.label.slice(0, 3)}</span>
                  </button>
                ))}
              </div>
              
              {formData.available_days?.length === 0 && (
                <p className="text-xs text-red-400 mt-2">
                  ⚠️ Selecione ao menos um dia
                </p>
              )}
            </div>

            {/* Horário Disponível */}
            <div className="mt-4" data-testid="category-time-window-section">
              <div className="flex items-start justify-between mb-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-300 flex items-center gap-2">
                    🕐 Horário Disponível <span className="text-xs text-gray-500 font-normal">(opcional)</span>
                  </label>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Quando esta categoria fica visível no cardápio. Deixe em branco para o dia todo.
                  </p>
                </div>
                {(formData.available_from || formData.available_until) && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, available_from: '', available_until: '' })}
                    className="text-xs text-amber-300 hover:text-amber-200 underline whitespace-nowrap"
                    data-testid="category-time-clear"
                  >
                    Limpar (dia todo)
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-wide text-gray-400 mb-1 block">De</label>
                  <input
                    type="time"
                    value={formData.available_from || ''}
                    onChange={(e) => setFormData({ ...formData, available_from: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 text-sm"
                    data-testid="category-available-from"
                  />
                </div>
                <span className="text-gray-500 pt-5">—</span>
                <div className="flex-1">
                  <label className="text-[10px] uppercase tracking-wide text-gray-400 mb-1 block">Até</label>
                  <input
                    type="time"
                    value={formData.available_until || ''}
                    onChange={(e) => setFormData({ ...formData, available_until: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 text-sm"
                    data-testid="category-available-until"
                  />
                </div>
              </div>
              {formData.available_from && formData.available_until && (
                <p className="text-[11px] text-blue-300 mt-2 flex items-center gap-1">
                  {(() => {
                    const f = formData.available_from;
                    const u = formData.available_until;
                    const fromMin = parseInt(f.slice(0,2))*60 + parseInt(f.slice(3,5));
                    const untilMin = parseInt(u.slice(0,2))*60 + parseInt(u.slice(3,5));
                    if (fromMin === untilMin) return '⚠️ Horários iguais — categoria ficará disponível o dia inteiro';
                    if (fromMin > untilMin) return `🌙 Horário noturno: das ${f} até ${u} do próximo dia`;
                    return `☀️ Disponível das ${f} às ${u}`;
                  })()}
                </p>
              )}
            </div>
            
            {/* Botões de ação */}
            <div className="flex justify-end space-x-3 pt-2 sticky bottom-0 bg-gray-900/95 backdrop-blur-sm py-3 -mx-2 px-2 border-t border-gray-700 mt-4">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button type="submit" className="glass-button-primary">
                {editingCategory ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default CategoriesContent;
